import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../components/setupAxios';
import API_URL from "../config";
import * as XLSX from 'xlsx';
import { Box, TextField, Button, Typography, Pagination,Table, TableHead, TableRow, TableCell, TableBody, TableContainer,Paper, LinearProgress  } from '@mui/material';

const ManageStudent = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [searchInput, setSearchInput] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchStudents();
  }, [searchQuery, currentPage]);

  const fetchStudents = async () => {
    try {
      const response = await api.get(
        `/auth/users?search=${searchQuery}`
      );
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleDelete = async (userId) => {
    if (window.confirm(`Are you sure you want to delete user ${userId}?`)) {
      try {
        await api.delete(`/auth/users/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        fetchStudents();
      } catch (error) {
        console.error('Error deleting student:', error);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert('Please select the file to upload first');
      return;
    }
  
    try {
      setUploading(true);
      setUploadProgress(0);
  
      // Wrap file reading and uploading in a Promise so that we await until all async tasks are complete.
      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  
            if (sheetData.length < 2) {
              alert('The file data is empty, please check.');
              return reject(new Error('Empty file data'));
            }
  
            // Validate headers
            const headers = sheetData[0].map(h => String(h).trim());
            const requiredHeaders = ["PERSON ID", "FIRST NAME", "LAST NAME"];
            const missingHeaders = requiredHeaders.filter(field => !headers.includes(field));
            if (missingHeaders.length > 0) {
              alert(
                `Please ensure your file includes the following columns:\n\n` +
                missingHeaders.join(", ")
              );
              return reject(new Error('Missing headers'));
            }
  
            // Process data rows (skip header)
            const rows = sheetData.slice(1);
            const studentList = rows.map((row) => {
              const idxPersonId = headers.indexOf('PERSON ID');
              const idxFirstName = headers.indexOf('FIRST NAME');
              const idxLastName = headers.indexOf('LAST NAME');
  
              const userIdValue = row[idxPersonId] ? String(row[idxPersonId]).trim() : '';
              const firstNameValue = row[idxFirstName] ? String(row[idxFirstName]).trim() : '';
              const lastNameValue = row[idxLastName] ? String(row[idxLastName]).trim() : '';
  
              return {
                userId: userIdValue,
                firstName: firstNameValue,
                lastName: lastNameValue,
                role: 'student',
                password: userIdValue,
              };
            });
  
            // Split studentList into chunks and upload each chunk sequentially.
            const chunkSize = 100;
            const total = studentList.length;
            let uploadedCount = 0;
  
            for (let startIndex = 0; startIndex < total; startIndex += chunkSize) {
              const chunk = studentList.slice(startIndex, startIndex + chunkSize);
              // Only the first chunk should trigger the deletion of all student records.
              const isFirstChunk = (startIndex === 0);
  
              await api.post(
                '/auth/bulk-upload',
                { students: chunk, isFirstChunk },
                {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }
              );
  
              uploadedCount += chunk.length;
              const percent = Math.round((uploadedCount / total) * 100);
              setUploadProgress(percent);
            }
  
            alert('Upload and import successful!');
            fetchStudents(); // Refresh the student list
            resolve();
          } catch (err) {
            reject(err);
          }
        };
  
        reader.onerror = (err) => reject(err);
        reader.readAsArrayBuffer(uploadFile);
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Upload failed:' + error.message);
    } finally {
      setUploading(false);
    }
  };
  
  const displayedStudents = students.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleRegisterClick = () => {
    navigate('/register');
};

return (
  <div>
    <div className="student-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box sx={{ width: '50%', mr: 2 }}>
          <TextField
            label="Search by User ID"
            variant="outlined"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ width: '50%' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ ml:1 }}
          >
            Search
          </Button>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          <Typography variant="subtitle1" mr={2}>
            Update information:
          </Typography>
          <Button variant="contained" component="label" sx={{ mr: 2 }}>
            Choose File
            <input
              type="file"
              accept=".xlsx, .xls, .csv"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {uploadFile ? uploadFile.name : 'No file selected'}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleUpload}>
            Upload
          </Button>
        </Box>
      </Box>

      {uploading && (
        <Box sx={{ width: '100%', mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading... {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      <div className="card">
        {displayedStudents.length === 0 ? (
          <Box textAlign="center" mt={2}>
            <Typography variant="body1">
              No information about this user?{' '}
              <Button variant="contained" onClick={handleRegisterClick}>
                Register
              </Button>{' '}
              here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
            <TableContainer component={Paper} sx={{ width: '100%' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">User ID</TableCell>
                    <TableCell align="center">First Name</TableCell>
                    <TableCell align="center">Last Name</TableCell>
                    <TableCell
                      align="center"
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedStudents.map((student) => (
                    <TableRow
                      key={student.userId}
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell align="center">
                        <Link to={`/student-details/${student.userId}`}>
                          {student.userId}
                        </Link>
                      </TableCell>
                      <TableCell align="center">{student.firstName}</TableCell>
                      <TableCell align="center">{student.lastName}</TableCell>
                      <TableCell
                        align="center"
                        sx={{ display: { xs: 'none', md: 'table-cell' } }}
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => handleDelete(student.userId)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </div>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(students.length / pageSize)}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
        />
      </Box>
    </div>
  </div>
);
};

export default ManageStudent;
